import { GroupDisplayName } from "@/components/display/GroupDisplayName";
import { FileCountBadge } from "@/components/folder/FileCountBadge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Folder } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faInfoCircle,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { UserDisplayName } from "../display/UserDisplayName";
import { FolderIcon } from "../display/display-icon/artifacts";

type FoldersProps = {
  folders: Folder[];
  actions?: (folder: Folder) => React.ReactNode;
} & (
  | {
      groupSlug: string;
      kitUuid: string;
    }
  | {
      groupSlug?: never;
      kitUuid?: never;
    }
);

export const Folders: React.FC<FoldersProps> = ({
  folders,
  groupSlug,
  kitUuid,
  actions,
}) => {
  const enableLink = groupSlug && kitUuid;

  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow className="text-muted-foreground font-family-fancy text-xs font-thin tracking-wide">
          <TableHead className="w-8"></TableHead>
          <TableHead className="w-80">Folders</TableHead>
          <TableHead className="w-30">Files</TableHead>
          <TableHead className="w-40">Author</TableHead>
          <TableHead className="w-40">Shared by</TableHead>
          <TableHead className="w-20 text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {folders.map((folder) => (
          <TableRow
            key={folder.uuid}
            link={
              enableLink
                ? {
                    to: "/w/$groupSlug/k/$kitUuid/knowledge/$folderUuid",
                    params: { groupSlug, kitUuid, folderUuid: folder.uuid },
                  }
                : undefined
            }
          >
            <TableCell>
              <FolderIcon />
            </TableCell>
            <TableCell>
              <div className="flex flex-row items-center justify-between overflow-hidden">
                <div className="flex-1 truncate font-bold">{folder.name}</div>
                <Tooltip>
                  <TooltipContent>
                    {folder.description || "No description"}
                  </TooltipContent>
                  <TooltipTrigger>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="ml-2 cursor-default"
                      disabled={!folder.description}
                      aria-label={folder.description || "No description"}
                    >
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </Button>
                  </TooltipTrigger>
                </Tooltip>
              </div>
            </TableCell>
            <TableCell>
              <FileCountBadge count={folder.files.length} />
            </TableCell>
            <TableCell className="truncate">
              {folder.user ? <UserDisplayName user={folder.user} /> : "-"}
            </TableCell>
            <TableCell className="truncate">
              {folder.group ? <GroupDisplayName group={folder.group} /> : "-"}
            </TableCell>
            <TableCell className="flex flex-row items-center justify-end gap-2">
              {enableLink && (
                <Button variant="ghost" size="icon" aria-label="Expand">
                  <FontAwesomeIcon icon={faEye} />
                </Button>
              )}
              {actions && actions(folder)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
